"use client";

import { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { BrandButton } from "@/components/ui/brand-button";
import { Upload } from "lucide-react";
import { RootState, useAppSelector } from "@/lib/redux/root";
import { useUpload } from "@/lib/hooks/useUpload";
import { useBrand } from "@/lib/hooks/useBrand";

export function FileUpload() {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [isDragging, setIsDragging] = useState(false);
	const { files } = useAppSelector((state: RootState) => state.upload);

	const {
		loading,
		location: { appInfo },
	} = useAppSelector((state) => (state as RootState).location);

	const { brandConfig } = useBrand();

	const { onSelectFiles, onDropFiles } = useUpload();

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	}, []);

	const handleDragLeave = useCallback((e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	}, []);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			setIsDragging(false);

			onDropFiles(e);
		},
		[onDropFiles]
	);

	// ------------------------------------------
	// File Select (Browse)
	// ------------------------------------------
	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		onSelectFiles(e);
	};

	const handleBrowseClick = () => {
		document.getElementById("file-input")?.click();
	};

	const handleUpload = useCallback(() => {
		if (files.length > 0) {
			const queryString = searchParams.toString();
			const url = queryString
				? `${location.pathname}/upload?${queryString}`
				: `${location.pathname}/upload`;
			navigate(url);
		}
	}, [files, navigate, location.pathname, searchParams]);

	// Auto navigate to upload screen
	useEffect(() => {
		if (files.length > 0) {
			handleUpload();
		}
	}, [files, handleUpload]);

	return (
		<div className='mx-auto w-full max-w-2xl'>
			{loading ? (
				<div className='h-[300px] w-full animate-pulse rounded bg-gray-300' />
			) : (
				<Card
					className={`min-height-[400px] relative flex flex-col items-center justify-center bg-white p-12 transition-all ${
						isDragging ? "border-2" : "border-2 border-dashed border-gray-200"
					}`}
					style={
						isDragging
							? {
									borderColor:
										appInfo?.primaryColor || brandConfig.primaryColor,
									backgroundColor: `${appInfo?.primaryColor || brandConfig.primaryColor}10`,
								}
							: undefined
					}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
					onDrop={handleDrop}
				>
					{/* Folder + file input */}
					<input
						id='file-input'
						type='file'
						multiple
						className='hidden'
						onChange={handleFileSelect}
					/>

					<div className='flex flex-col items-center gap-6'>
						<div className='text-center'>
							<h2 className='mb-2 text-2xl font-semibold text-gray-800'>
								<span className='font-bold'>Drag & Drop</span> files or folders
							</h2>
							<p className='text-gray-600'>
								or{" "}
								<button
									onClick={handleBrowseClick}
									className='font-semibold underline'
									style={{ color: appInfo?.textColor || brandConfig.textColor }}
									onMouseEnter={(e) =>
										(e.currentTarget.style.color =
											appInfo?.hoverColor || brandConfig.hoverColor)
									}
									onMouseLeave={(e) =>
										(e.currentTarget.style.color =
											appInfo?.textColor || brandConfig.textColor)
									}
								>
									Browse for files
								</button>
							</p>
						</div>

						{/* <BrandButton
							onClick={handleBrowseClick}
							size="icon"
							className="h-22 w-22 rounded-full shadow-lg [&_svg]:size-7"
						>
							<Upload className="text-white" />
						</BrandButton> */}
						<div
							className='group relative inline-block h-30 w-30 cursor-pointer'
							onClick={handleBrowseClick}
						>
							{/* Default SVG (slightly right) */}
							<img
								src='/Dropplus.svg'
								alt='default icon'
								className='absolute inset-0 h-full w-full translate-x-1 object-contain group-hover:hidden'
							/>

							{/* Hover SVG (centered) */}
							<img
								src='/Drop.svg'
								alt='hover icon'
								className='absolute inset-0 hidden h-full w-full object-contain group-hover:block'
							/>
						</div>
					</div>
				</Card>
			)}
		</div>
	);
}
